import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import io from 'socket.io-client';

interface AuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  userData: {
    id: string | number;
    nama: string;
    jabatan: string;
    dealer_code: string;
    otoritas: string;
    kode_grup1: string;
    warehouse_code: string;
    dealer_name?: string;
    warehouse_name?: string;
    menu_otoritas?: string;
    users?: string;
    last_activity_timestamp?: string;
  } | null;
  login: (token: string) => void;
  logout: () => void;
  fetchUserData: () => Promise<void>;
  socket: any;
  isOffline: boolean;
  isMaintenance: boolean;
}

const AuthContext = createContext<AuthContextType>({} as AuthContextType);


const checkAuthToken = async (token: string) => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_BACKEND_URL}/api/auth/validate-token`,
      {
        headers: { 'Authorization': `Bearer ${token}` }
      }
    );
    if (!response.ok) return false;
    const data = await response.json();
    return data.valid === true;
  } catch (error) {
    return false;
  }
};


export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [userData, setUserData] = useState<{
    id: string | number;
    nama: string;
    jabatan: string;
    dealer_code: string;
    otoritas: string;
    kode_grup1: string;
    warehouse_code: string;
    dealer_name?: string;
    warehouse_name?: string;
    menu_otoritas?: string;
    users?: string;
    last_activity_timestamp?: string;
  } | null>(null);
  const [socket, setSocket] = useState<any>(null);
  const [isOffline, setIsOffline] = useState(false);
  const [isMaintenance, setIsMaintenance] = useState(false);
  const timerRef = useRef<any>(null);
  const checkClientInternet = async (): Promise<boolean> => {
    if (!navigator.onLine) return false;
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 3000);
      await fetch('https://clients3.google.com/generate_204', {
        mode: 'no-cors',
        signal: controller.signal,
        cache: 'no-store'
      });
      clearTimeout(timeoutId);
      return true;
    } catch (e) {
      return false;
    }
  };


  const logout = useCallback(() => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('lastActivity');
    setIsAuthenticated(false);
    setUserData(null);
    navigate('/login_page');
  }, [navigate]);


  const fetchUserData = useCallback(async () => {
    if (!navigator.onLine) return;
    const token = localStorage.getItem('authToken');
    if (!token || token === 'null' || token === 'undefined') {
      logout();
      return;
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/auth/user-info`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      if (!response.ok) {
        if (response.status === 401) logout();
        throw new Error('Failed to fetch user data');
      }

      const data = await response.json();
      setUserData({
        id: data.id,
        nama: data.nama,
        jabatan: data.jabatan,
        dealer_code: data.dealer_code,
        otoritas: data.otoritas,
        kode_grup1: data.kode_grup1,
        warehouse_code: data.warehouse_code,
        dealer_name: data.dealer_name,
        warehouse_name: data.warehouse_name,
        menu_otoritas: data.menu_otoritas,
        users: data.users,
        last_activity_timestamp: data.last_activity_timestamp
      });
      setIsAuthenticated(true);
    } catch (error) {
      console.error('Fetch user data network error:', error);
    }
  }, [logout]);


  const login = useCallback(async (token: string) => {
    localStorage.setItem('authToken', token);
    setIsAuthenticated(true);
    await fetchUserData();
    navigate('/dashboard');
    setTimeout(() => navigate('/dashboard'), 300);
  }, [fetchUserData, navigate]);


  useEffect(() => {
    const verifyToken = async () => {
      const token = localStorage.getItem('authToken');
      console.log('Stored token:', token);

      if (token && token !== 'null' && token !== 'undefined') {
        try {
          const isValid = await checkAuthToken(token);
          console.log('Token validity:', isValid);

          setIsAuthenticated(isValid);
          if (isValid) {
            await fetchUserData();
          } else {
            logout();
          }
        } catch (error) {
          console.error('Token verification failed:', error);
          logout();
        }
      } else {
        logout();
      }
      setIsLoading(false);
    };
    verifyToken();
  }, [fetchUserData, logout]);

  useEffect(() => {
    if (isAuthenticated) {
      const interval = setInterval(fetchUserData, 60000);
      return () => clearInterval(interval);
    }
  }, [isAuthenticated, fetchUserData]);

  useEffect(() => {
    if (isAuthenticated && userData?.kode_grup1) {
      const socketUrl = process.env.REACT_APP_BACKEND_URL || '';
      const newSocket = io(socketUrl, { transports: ['websocket'], reconnectionDelay: 1000, reconnectionDelayMax: 5000 });
      setSocket(newSocket);

      const handleDisconnectOrError = () => {
        if (timerRef.current) return;
        let elapsed = 0;
        timerRef.current = setInterval(async () => {
          elapsed += 2;
          const hasInternet = await checkClientInternet();
          if (hasInternet) {
            if (elapsed >= 10) {
              setIsMaintenance(true);
              setIsOffline(false);
              clearInterval(timerRef.current);
              timerRef.current = null;
            }
          } else {
            if (elapsed >= 90) {
              setIsOffline(true);
              setIsMaintenance(false);
              clearInterval(timerRef.current);
              timerRef.current = null;
            }
          }
        }, 2000);
      };

      newSocket.on('connect', () => {
        if (timerRef.current) {
          clearInterval(timerRef.current);
          timerRef.current = null;
        }
        setIsOffline(false);
        setIsMaintenance(false);
      });
      newSocket.on('version_check', async (serverVersion: string) => {
        const CLIENT_VERSION = process.env.REACT_APP_VERSION || "1.0.2";
        if (serverVersion !== CLIENT_VERSION) {
          const lastAttempt = sessionStorage.getItem('version_retry');
          if (lastAttempt === serverVersion) {
            console.warn(`Loop blocked for version ${serverVersion}`);
            return;
          }
          if ('caches' in window) {
            const cacheNames = await caches.keys();
            await Promise.all(cacheNames.map(name => caches.delete(name)));
          }
          if ('serviceWorker' in navigator) {
            const registrations = await navigator.serviceWorker.getRegistrations();
            await Promise.all(registrations.map(r => r.unregister()));
          }
          const token = localStorage.getItem('authToken');
          localStorage.clear();
          if (token) localStorage.setItem('authToken', token);
          sessionStorage.clear();
          sessionStorage.setItem('version_retry', serverVersion);
          const bypassUrl = new URL(window.location.origin + window.location.pathname);
          bypassUrl.searchParams.set('v', Date.now().toString());
          window.location.href = bypassUrl.toString() + window.location.hash;
        }
      });
      newSocket.on('disconnect', (reason) => {
        if (reason === 'transport close' || reason === 'io client disconnect') {
          handleDisconnectOrError();
        }
      });
      newSocket.on('connect_error', () => {
        handleDisconnectOrError();
      });
      const reportStatus = (status: 'active' | 'inactive') => {
        newSocket.emit('register_user', { userId: userData.kode_grup1, status });
      };
      reportStatus(document.visibilityState === 'visible' ? 'active' : 'inactive');
      const handleVisibilityChange = () => {
        const status = document.visibilityState === 'visible' ? 'active' : 'inactive';
        newSocket.emit('update_visibility', { status });
      };
      document.addEventListener('visibilitychange', handleVisibilityChange);
      return () => {
        document.removeEventListener('visibilitychange', handleVisibilityChange);
        newSocket.disconnect();
        setSocket(null);
      };
    }
  }, [isAuthenticated, userData?.kode_grup1]);

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        isLoading,
        userData,
        login,
        logout,
        fetchUserData,
        socket,
        isOffline,
        isMaintenance
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);